// Whoop-style 4-tile dashboard strip. Each tile is a ring card: eyebrow
// label, big ring with score in centre, optional delta + sub below.
//
//   1. Reading score (30d avg)
//   2. This week  (current ISO week avg)
//   3. Top dimension
//   4. Watch dimension
//
// Streak intentionally removed: meetings don't happen daily and people
// don't work weekends. Punishing a non-meeting day is the wrong incentive.

import { ScoreRing } from "./ScoreRing";

type Direction = "up" | "down" | "flat";

type Delta = { text: string; direction: Direction } | undefined;

function deltaFor(n: number | null): Delta {
  if (n == null) return undefined;
  if (Math.abs(n) < 0.5) return { text: "flat", direction: "flat" };
  return {
    text: `${n > 0 ? "↑ +" : "↓ "}${Math.abs(Math.round(n))}`,
    direction: n > 0 ? "up" : "down",
  };
}

export type HeroStripProps = {
  ringScore: number;
  ringDelta: number | null;
  thisWeek: { avg: number; deltaVsPrior: number | null };
  topDim: { score: number; name: string; deltaVsPrior: number | null };
  watchDim: { score: number; name: string; deltaVsPrior: number | null };
};

export function HeroStrip({ ringScore, ringDelta, thisWeek, topDim, watchDim }: HeroStripProps) {
  return (
    <section className="strip">
      <RingTile
        label="Reading score"
        score={ringScore}
        variant={ringVariant(ringScore)}
        sub="30-day average"
        delta={deltaFor(ringDelta)}
      />
      <RingTile
        label="This week"
        score={thisWeek.avg}
        variant={ringVariant(thisWeek.avg)}
        sub="weekly average"
        delta={deltaFor(thisWeek.deltaVsPrior)}
      />
      <RingTile
        label="Top dimension"
        score={topDim.score}
        variant="sage"
        sub={topDim.name}
        delta={deltaFor(topDim.deltaVsPrior)}
      />
      <RingTile
        label="Watch"
        score={watchDim.score}
        variant="warn"
        sub={watchDim.name}
        delta={deltaFor(watchDim.deltaVsPrior)}
      />
    </section>
  );
}

function RingTile({
  label,
  score,
  variant,
  sub,
  delta,
}: {
  label: string;
  score: number;
  variant: "ink" | "sage" | "warn";
  sub?: string;
  delta?: Delta;
}) {
  return (
    <div className="ring-tile">
      <span className="l">{label}</span>
      <ScoreRing score={score} variant={variant} />
      <div className="ring-foot">
        {delta ? <span className={`delta ${delta.direction}`}>{delta.text}</span> : <span className="delta flat">·</span>}
        {sub ? <span className="sub">{sub}</span> : null}
      </div>
    </div>
  );
}

function ringVariant(score: number): "ink" | "sage" | "warn" {
  if (score >= 70) return "sage";
  if (score < 55) return "warn";
  return "ink";
}
